<template>
	<BaseLayout
		:footer-full="true"
		:include-padding="true"
		:background-color-class="`bg-color-white`"
		:content-full-height="true"
	>
		<header class="category-header container">
			<div
				class="display-flex align-items-center align-items-flex-end-lg justify-content-space-between flex-wrap-wrap bb pb-100 mb-150"
			>
				<BaseCtaLink
					:link="blogLandingLink"
					classes="blog-back-link mb-100-smo"
					icon="arrow"
					icon-dir="left"
					:is-leading-icon="true"
				/>
				<div class="category-header__title text-align-center-md px-100-lg">
					<h1 class="heading-2 mb-50-smo">
						<BasePlaceHolderInline width="20rem" :placehold="loading" theme="light"
							>{{ category?.name || '' }}
						</BasePlaceHolderInline>
					</h1>
					<p class="display-none display-block-lg body-md mt-50">
						<BasePlaceHolderInline width="36rem" :placehold="loading" theme="light">
							{{ category?.description }}
						</BasePlaceHolderInline>
					</p>
				</div>
				<p v-if="categoryBlogs" class="mt-0 dark">
					<BasePlaceHolderInline width="8rem" :placehold="loading" theme="light"
						>{{ pageStart }}-{{ pageEnd }} of {{ categoryBlogs.length }} articles</BasePlaceHolderInline
					>
				</p>
			</div>
		</header>

		<div v-if="loading" class="pt-300"></div>

		<BasePlaceHolder :section-count="4" :line-count="4" class="container" :placehold="loading">
			<section class="category-articles">
				<ArticleCardHorizontal
					v-for="blog in paginatedData"
					:key="blog._id"
					:article="blog"
					:classes="`category-article pt-150 pb-150`"
					:show-date="true"
				/>
				<ListPaginator v-model:paginator="paginator" />
			</section>
		</BasePlaceHolder>
	</BaseLayout>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { getBlogArticlesByCategory, getCategoryBySlug } from '@api/sanity/graphql/blog-education-center';
import { SanityArticle, SanityBlogCategory } from 'types/sanity/blog-education-center';
import { useRoute, useRouter } from 'vue-router';
import ArticleCardHorizontal from '@components/blog/article-card-horizontal.vue';
import { Link } from 'types/layout';
import ListPaginator from '@components/layout/list-paginator.vue';
import Paginator from '@classes/Paginator';
import { useHead } from '@unhead/vue';

const route = useRoute();
const router = useRouter();
const categorySlug = ref(route.params.categoryUrlSlug as string);

const blogLandingLink: Link = {
	text: 'Education Center',
	router: 'blog-landing'
};

const categoryBlogs = ref<SanityArticle[] | null>(null);
const paginator = ref<Paginator>(new Paginator(0, 10, 0));

const loading = ref(true);

const paginatedData = computed((): Array<SanityArticle> => {
	return categoryBlogs.value ? categoryBlogs.value.slice(paginator.value.start, paginator.value.end) : [];
});

const pageStart = computed((): number => {
	return paginator.value.start + 1;
});

const pageEnd = computed((): number => {
	return Math.min(paginator.value.end, paginator.value.totalElements);
});

const category = ref<SanityBlogCategory | null>(null);

const fetchBlogsAndCategory = async (slug: string) => {
	// Get all categories first and find the one with the matching slug for category details
	const getActiveCategory = await getCategoryBySlug(slug);

	if (getActiveCategory) {
		category.value = getActiveCategory;
		const fetchedCategoryBlogs = await getBlogArticlesByCategory(getActiveCategory.slug?.current ?? '');

		if (fetchedCategoryBlogs.length > 0) {
			categoryBlogs.value = fetchedCategoryBlogs;
		}
	} else {
		category.value = null;
		categoryBlogs.value = null;
	}
};

watch(
	() => categoryBlogs.value,
	(newCategoryBlogs) => {
		if (newCategoryBlogs) {
			paginator.value = new Paginator(0, 10, newCategoryBlogs.length);
		}
	}
);

async function setup(): Promise<void> {
	try {
		await fetchBlogsAndCategory(categorySlug.value);
	} catch (error) {
		console.error('Error loading category data:', error);
		await router.push({ path: '/404' });
		return;
	}

	if (category.value) {
		useHead({
			title: category.value?.name,
			meta: [
				{ property: 'og:title', content: category.value?.name },
				{ name: 'description', content: category.value?.description },
				{ property: 'og:description', content: category.value?.description }
			]
		});
	}
	loading.value = false;
}

setup();
</script>

<style lang="scss">
@use '../../styles/utilities/respond-to.scss' as *;

.category-header {
	.blog-back-link {
		display: flex;
		align-items: center;

		svg {
			transform: rotate(180deg);
		}
	}

	&__title {
		flex: 1 1 min-content;
		@include respond-to(smo) {
			flex: 1 100%;
			width: 100%;
		}
	}
}

.category-article {
	margin-bottom: 0;
	margin-top: 0;
	position: relative;

	&::after {
		background-image: $dotted-border-gradient;
		background-position: top;
		background-size: 4px 2px;
		background-repeat: repeat-x;
		bottom: 0;
		content: '';
		left: 0;
		height: 2px;
		position: absolute;
		width: 100%;
	}

	&:last-of-type {
		&::after {
			display: none;
		}
	}
}
</style>
